import { watch, ref } from 'vue';
import JitsiService from '@/Services/JitsiService';
import CroppedScreenShareEffect from '@/Services/CroppedScreenShareEffect';
import useSessionStore from '@/Stores/useSessionStore';
import { COMMANDS, DISPLAY_NAME, VICE_SESSION_PHASES } from '@/Utils/vice-constants';

function useJitsiScreenshare(screenshareId, shouldEnablePartialScreenshare = false) {
  const screenShareImage = ref('');
  let jitsiRoom;
  let jitsiVideoTrack;
  let jitsiAudioTrack;

  const sessionStore = useSessionStore();
  sessionStore.screenShares[screenshareId] = {};

  // When kicking the screenshare user, we just set sessionStore.screenshareOngoing = false
  // This watcher checks if the newState is false, it means a screenshare user was turned on
  // and now being turned off. Stop the screenshare in this instance. prevents screenshare from
  // moving to the next phase
  watch(
    () => sessionStore.screenShares[screenshareId].screenshareOngoing,
    async (newState) => {
      if (!newState) {
        await stopScreenshareStream();
      }
    },
  );

  const addTrackToRoom = async () => {
    await jitsiRoom.addTrack(jitsiVideoTrack);
    if (jitsiAudioTrack) {
      await jitsiRoom.addTrack(jitsiAudioTrack);
    }
    sessionStore.screenShares[screenshareId].screenshareOngoing = true;
    sendScreenshareEndedUpdate(false);
  };

  const stopScreenshareStream = async () => {
    // remove video track
    if (jitsiVideoTrack === undefined) return;
    await jitsiVideoTrack.setEffect(undefined);
    await jitsiVideoTrack.dispose();
    await jitsiRoom.removeTrack(jitsiVideoTrack);

    jitsiVideoTrack.stream.getTracks().forEach((track) => track.stop());
    jitsiVideoTrack.track.stop();

    // remove audio track
    if (jitsiAudioTrack) {
      await jitsiRoom.removeTrack(jitsiVideoTrack);
      await jitsiRoom.removeTrack(jitsiAudioTrack);

      jitsiAudioTrack.stream.getTracks().forEach((track) => track.stop());
    }

    if (jitsiRoom.room !== null) {
      jitsiRoom.leave();
    }
    sessionStore.screenShares[screenshareId].screenshareOngoing = false;
    screenShareImage.value = '';
  };

  // The TRACK_REMOVED handler is not called for the participants
  // so that the screenshare ui will be removed so we send an event to do that.
  const sendScreenshareEndedUpdate = (isEnded) => {
    jitsiRoom.sendCommandOnce(COMMANDS.END_SCREENSHARE, {
      value: COMMANDS.END_SCREENSHARE,
      attributes: {
        isEnded,
      },
      children: [],
    });
  };

  const addVideoTrackListeners = () => {
    jitsiVideoTrack.on(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, async () => {
      sendScreenshareEndedUpdate(true);
      await stopScreenshareStream();
    });
  };

  const getImageFromVideoStream = async (stream) => {
    const canvas = document.createElement('canvas');
    if ('ImageCapture' in window) {
      const videoTrack = stream.getVideoTracks()[0];
      const imageCapture = new window.ImageCapture(videoTrack);
      const bitmap = await imageCapture.grabFrame();
      canvas.height = bitmap.height;
      canvas.width = bitmap.width;
      canvas.getContext('2d').drawImage(bitmap, 0, 0);
      return canvas.toDataURL();
    }
    const video = document.createElement('video');
    video.srcObject = stream;
    return new Promise((resolve, reject) => {
      video.addEventListener('loadeddata', async () => {
        const { videoWidth, videoHeight } = video;
        canvas.width = videoWidth;
        canvas.height = videoHeight;

        try {
          await video.play();
          canvas.getContext('2d').drawImage(video, 0, 0, videoWidth, videoHeight);
          return resolve(canvas.toDataURL());
        } catch (error) {
          return reject(error);
        }
      });
    });
  };

  const createNewScreenShareUser = async (isPartialScreen) => {
    try {
      const jitsi = JitsiService();
      const { video: videoTrack, audio } = await jitsi.createScreenShareTracks();

      jitsiVideoTrack = videoTrack;
      jitsiAudioTrack = audio;
      const { room } = await jitsi.makeConnection(sessionStore.sessionId);
      jitsiRoom = room;
      if (
        shouldEnablePartialScreenshare &&
        sessionStore.currentPhase == VICE_SESSION_PHASES.CONSULTATION &&
        isPartialScreen
      ) {
        screenShareImage.value = await getImageFromVideoStream(jitsiVideoTrack.stream);
      } else {
        room.setDisplayName(`${DISPLAY_NAME.SCREENSHARE_USER}_${screenshareId}`);
        room.join();
        await addTrackToRoom();
        addVideoTrackListeners();
      }
    } catch (error) {
      console.log('createNewScreenShareUser error: ', error);
    }
  };

  const toggleScreenshare = async (isPartialScreen = false) => {
    if (sessionStore.screenShares[screenshareId].screenshareOngoing) {
      await stopScreenshareStream();
    } else if (JitsiMeetJS.isDesktopSharingEnabled()) {
      await createNewScreenShareUser(isPartialScreen);
    }
  };

  const postProcessScreenshare = async (coordinates) => {
    jitsiRoom.setDisplayName(`${DISPLAY_NAME.SCREENSHARE_USER}_${screenshareId}`);
    jitsiRoom.join();

    await addTrackToRoom();
    addVideoTrackListeners();

    setTimeout(async () => {
      const newScreenShareEffect = new CroppedScreenShareEffect(coordinates);
      const isEnabled = newScreenShareEffect.isEnabled(jitsiVideoTrack);
      if (isEnabled) {
        await jitsiVideoTrack?.setEffect(newScreenShareEffect);
      }
    }, 3000);
    screenShareImage.value = '';
  };

  return {
    toggleScreenshare,
    stopScreenshareStream,
    postProcessScreenshare,
    screenShareImage,
    jitsiVideoTrack,
  };
}

export default useJitsiScreenshare;
